import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { TaskEditorState } from "../utils/taskEditorUtils";
import { cloneTaskEditorState } from "../utils/taskEditorDiff";
import { resumeTaskAuthoring, streamTaskAuthoring } from "./apiClient";
import {
  clearChat,
  loadLatestChat,
  loadSpecificChat,
  persistTurn,
} from "./chatStorage";
import type {
  AssistantStep,
  AssistantTurn,
  ChatTurn,
  Decision,
  SSEEvent,
  TaskBlocksPatch,
  UserTurn,
} from "./types";

// 이벤트가 한 건도 안 오는 서버 hang 을 끊는 idle 타임아웃.
const IDLE_TIMEOUT_MS = 60_000;
// final/error 수신 후 trailing debug 이벤트를 잠깐 기다렸다가 연결을 정리하는 grace.
const POST_FINAL_GRACE_MS = 2_000;

const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const generateThreadId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return rid() + "-" + rid();
};

interface UseAgentChatThreadResult {
  threadId: string | null;
  turns: ChatTurn[];
  isStreaming: boolean;
  // BE 에서 thread 내역을 가져오는 동안 true — 초기 진입/활동지 전환/thread 전환 시.
  isLoadingHistory: boolean;
  send: (message: string, currentTask?: TaskEditorState) => Promise<void>;
  // 마지막 user turn 을 수정해 재실행 — 활동지를 그 직전(taskBefore)으로 되돌리고
  // checkpoint 절단(rewind) 후 같은 turn 슬롯으로 재전송.
  editLastUserTurn: (
    newMessage: string,
    currentTask?: TaskEditorState
  ) => Promise<void>;
  respondToApproval: (
    turnId: string,
    decisions: Decision[],
    currentTask?: TaskEditorState
  ) => Promise<void>;
  clear: () => void;
  cancel: () => void;
  // 턴 단위 revert UI 에서 사용 — taskBefore 가 저장된 턴을 찾아 호출자가 적용한다.
  revertTurn: (turnId: string) => TaskEditorState | undefined;
  // 이전 대화 드롭다운에서 다른 thread 로 swap. 진행 중이면 abort 후 swap.
  switchThread: (targetThreadId: string) => Promise<void>;
}

interface UseAgentChatThreadArgs {
  // 턴 시작/종료 콜백 — sync state 의 markTurnStart/End 와 연결한다.
  onTurnStart?: () => void;
  onTurnEnd?: () => void;
  // 마지막 요청 수정 시 활동지를 taskBefore 로 복원하고 working_store PUT 완료까지 await —
  // rewind 스트림이 복원본을 읽도록 재실행 전 순서를 보장한다.
  revertTaskBefore?: (taskBefore: TaskEditorState) => Promise<void>;
}

export function useAgentChatThread(
  taskId: string | undefined,
  onTaskPatch: (
    patch: TaskBlocksPatch,
    meta?: { version?: number; revision?: string }
  ) => void,
  args: UseAgentChatThreadArgs = {}
): UseAgentChatThreadResult {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // 첫 페인트에 taskId 가 이미 있으면 곧바로 loadLatestChat 이 돌므로 true 로 시작 —
  // 그렇지 않으면 effect 가 도는 한 프레임 동안 EmptyState(시나리오 버튼)가 깜빡인다.
  const [isLoadingHistory, setIsLoadingHistory] = useState(() => !!taskId);
  const abortRef = useRef<AbortController | null>(null);
  const onTaskPatchRef = useRef(onTaskPatch);
  const onTurnStartRef = useRef(args.onTurnStart);
  const onTurnEndRef = useRef(args.onTurnEnd);
  const revertTaskBeforeRef = useRef(args.revertTaskBefore);

  useEffect(() => {
    onTaskPatchRef.current = onTaskPatch;
  }, [onTaskPatch]);

  useEffect(() => {
    onTurnStartRef.current = args.onTurnStart;
    onTurnEndRef.current = args.onTurnEnd;
    revertTaskBeforeRef.current = args.revertTaskBefore;
  }, [args.onTurnStart, args.onTurnEnd, args.revertTaskBefore]);

  // taskId 변경 시 BE 에서 가장 최근(active) thread 의 turns 를 가져온다 (network).
  // race: 마운트 도중 taskId 가 또 바뀌면 stale 응답 무시.
  // useLayoutEffect 인 이유: taskId 가 undefined→정의됨 으로 바뀌는 프레임에서 turns=[] +
  // loading=true 가 paint 전에 커밋돼야 EmptyState(시나리오 버튼) 깜빡임이 없다.
  useLayoutEffect(() => {
    if (!taskId) {
      setThreadId(null);
      setTurns([]);
      setIsLoadingHistory(false);
      return;
    }
    let cancelled = false;
    setTurns([]); // 로딩 동안 비어 보이게
    setThreadId(null);
    setIsLoadingHistory(true);
    void loadLatestChat(taskId).then((loaded) => {
      if (cancelled) return;
      setIsLoadingHistory(false);
      if (loaded) {
        // BE 에서 막 받아온 turn 은 이미 sync 상태. persist effect 가 이걸 다시 PUT 하면
        // BE 가 turn 마다 thread.updated_at 을 NOW 로 갱신해 시간 표시가 매번 "방금" 으로
        // 갱신됨. 로드한 turn 들의 signature 를 미리 등록해 skip 시킨다.
        const sigs = new Map<string, string>();
        loaded.turns.forEach((t) => sigs.set(t.id, JSON.stringify(t)));
        persistedSignaturesRef.current = sigs;
        setThreadId(loaded.threadId);
        setTurns(loaded.turns);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  // turn 추가/변경 시 BE 에 upsert. localStorage 와 달리 fire-and-forget — 실패는 콘솔만,
  // UI 는 그대로 (best-effort). sequence = 배열 index + 1 (1-based, BE 요구).
  //
  // 무차별 매 이벤트마다 PUT 보내면 SSE 토큰 chunk 마다 네트워크 부담 → 500ms debounce.
  // signature (JSON) 캐시로 unchanged turn 은 PUT 생략.
  const persistedSignaturesRef = useRef<Map<string, string>>(new Map());
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!taskId || !threadId) return;
    const tid = threadId; // for closure
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const sigs = persistedSignaturesRef.current;
      turns.forEach((turn, index) => {
        const sig = JSON.stringify(turn);
        if (sigs.get(turn.id) === sig) return;
        sigs.set(turn.id, sig);
        // fire-and-forget — 실패해도 다음 변경 시 재시도된다 (signature 가 다르므로).
        void persistTurn(tid, taskId, turn, index + 1);
      });
    }, 500);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [taskId, threadId, turns]);

  // assistantTurn 갱신 reducer
  const applyEvent = useCallback((turnId: string, event: SSEEvent) => {
    // keepalive 는 idle 타이머 리셋(consumeStream 의 armIdle)이 유일한 목적 —
    // turn state 변화가 없으므로 setTurns 자체를 호출하지 않는다.
    if (event.event === "keepalive") return;
    setTurns((prev) =>
      prev.map((turn) => {
        if (turn.role !== "assistant" || turn.id !== turnId) return turn;
        return reduceAssistantTurn(turn, event);
      })
    );
    if (event.event === "task_patch") {
      onTaskPatchRef.current(event.data.task, {
        version: event.data.version,
        revision: event.data.revision,
      });
    }
  }, []);

  const consumeStream = useCallback(
    async (
      turnId: string,
      stream: AsyncGenerator<SSEEvent>,
      controller: AbortController
    ) => {
      const signal = controller.signal;
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      let timedOut = false;
      let finalized = false;

      // 이벤트가 올 때마다 idle 타이머 리셋. 만료되면 turn 을 timeout 에러로
      // 표시하고 연결을 끊는다. (abort 시 제너레이터는 throw 없이 정상 종료되므로
      // 에러 표시는 여기서 직접 한다 — catch 에 의존하지 않음.)
      const armIdle = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          timedOut = true;
          setTurns((prev) =>
            prev.map((turn) =>
              turn.role === "assistant" &&
              turn.id === turnId &&
              turn.status === "streaming"
                ? {
                    ...turn,
                    status: "error",
                    error: {
                      type: "timeout",
                      message: "응답 시간이 초과됐어요. 다시 시도해 주세요.",
                    },
                  }
                : turn
            )
          );
          setIsStreaming(false);
          controller.abort();
        }, IDLE_TIMEOUT_MS);
      };

      try {
        armIdle();
        for await (const event of stream) {
          // finalized 후엔 grace 타이머가 연결 정리를 담당하므로 idle 재무장 안 함.
          if (!finalized) armIdle();
          applyEvent(turnId, event);
          // abort(정지/타임아웃/grace) 후에도 postSSE 가 flush 한 잔여 이벤트는 적용하고 종료한다.
          // 적용 전에 break 하면 정지 직전 수신된(BE 에 이미 commit 된) task_patch 가 유실돼
          // 우측 패널이 stale 로 남는다 — abort 체크를 applyEvent 뒤로 둔다.
          if (signal.aborted) break;
          if (event.event === "final" || event.event === "error") {
            // 논리적 종료 — 스피너 즉시 해제. 연결은 trailing debug 를 위해
            // 짧은 grace 뒤 정리한다 (서버가 close 안 해도 무한 대기 방지).
            finalized = true;
            setIsStreaming(false);
            onTurnEndRef.current?.();
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(
              () => controller.abort(),
              POST_FINAL_GRACE_MS
            );
          }
        }
      } catch (err) {
        // abort(타임아웃/사용자취소/grace)·정상종료는 throw 하지 않는다.
        // 여기 오는 건 진짜 네트워크/스트림 오류뿐. timeout 은 armIdle 에서 이미 처리.
        if (!finalized && !timedOut && !signal.aborted) {
          const message = err instanceof Error ? err.message : String(err);
          setTurns((prev) =>
            prev.map((turn) =>
              turn.role === "assistant" && turn.id === turnId
                ? {
                    ...turn,
                    status: "error",
                    error: { type: "stream_error", message },
                  }
                : turn
            )
          );
        }
        // finally 에서도 turn end 콜백을 호출하는데, 정상 final/error 경로는 위에서 이미
        // 호출됐다. ref 호출 자체는 idempotent 하지만 markTurnEnd 가 중복돼도 안전 (clear-on-null).
      } finally {
        if (idleTimer) clearTimeout(idleTimer);
        // 잔여 연결이 정리되는 사이 새 turn 이 시작됐다면 그쪽 스피너를 끄지 않는다.
        if (abortRef.current === controller) setIsStreaming(false);
        if (!finalized) onTurnEndRef.current?.();
      }
    },
    [applyEvent]
  );

  const send = useCallback(
    async (message: string, currentTask?: TaskEditorState) => {
      if (isStreaming) return;
      let tid = threadId;
      if (!tid) {
        tid = generateThreadId();
        setThreadId(tid);
      }
      const userTurn: UserTurn = {
        id: rid(),
        role: "user",
        content: message,
        createdAt: Date.now(),
      };
      // 턴 시작 시점의 task 를 snapshot — 추후 "이 턴 되돌리기" 의 복원 대상.
      const taskBefore = currentTask
        ? cloneTaskEditorState(currentTask)
        : undefined;
      const assistantTurn: AssistantTurn = {
        id: rid(),
        role: "assistant",
        steps: [],
        status: "streaming",
        taskChanged: false,
        taskBefore,
        createdAt: Date.now(),
      };
      setTurns((prev) => [...prev, userTurn, assistantTurn]);
      setIsStreaming(true);

      // sync state 에 base 등록 — agent patch 도착 시 3-way merge 의 base 가 됨.
      onTurnStartRef.current?.();

      // 직전 turn 의 잔여 연결(서버가 close 안 한 경우)을 정리하고 시작.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // BE 가 working_task store 에서 task_id 의 blocks 를 fetch 해 vfs hydrate 한다.
      // current_task 송신 폐기 — task_id 만 보내면 BE 가 single source 로 작동.
      const stream = streamTaskAuthoring(
        {
          thread_id: tid,
          message,
          task_id: currentTask?.task_id,
        },
        controller.signal
      );
      await consumeStream(assistantTurn.id, stream, controller);
    },
    [consumeStream, isStreaming, threadId]
  );

  const editLastUserTurn = useCallback(
    async (newMessage: string, currentTask?: TaskEditorState) => {
      if (isStreaming || !threadId) return;
      const last = turns[turns.length - 1];
      const prevUser = turns[turns.length - 2];
      // 마지막 turn 이 종료된 assistant 이고 그 직전이 user 일 때만 — streaming/awaiting_approval 중엔 불가.
      if (
        !last ||
        last.role !== "assistant" ||
        (last.status !== "complete" &&
          last.status !== "error" &&
          last.status !== "cancelled") ||
        !prevUser ||
        prevUser.role !== "user"
      ) {
        return;
      }
      const trimmed = newMessage.trim();
      if (!trimmed) return;

      // 1) 활동지를 그 요청 직전(taskBefore)으로 복원하고 working_store PUT 완료까지 대기 —
      //    BE rewind 스트림이 복원본 blocks 를 읽도록 순서 보장.
      if (last.taskBefore) {
        await revertTaskBeforeRef.current?.(last.taskBefore);
      }

      // 2) 마지막 user/assistant turn 을 같은 id 로 제자리 리셋(BE chat_turns 덮어쓰기 → 고아 없음).
      const assistantId = last.id;
      setTurns((prev) =>
        prev.map((turn, index) => {
          if (index === prev.length - 2 && turn.role === "user") {
            return { ...turn, content: trimmed };
          }
          if (index === prev.length - 1 && turn.role === "assistant") {
            // id 와 taskBefore 유지 — 복원 후 task == taskBefore 라 재실행 base/diff anchor 로 그대로 유효.
            return {
              ...turn,
              steps: [],
              status: "streaming",
              taskChanged: false,
              todos: undefined,
              pendingApproval: undefined,
              debug: undefined,
              error: undefined,
            };
          }
          return turn;
        })
      );
      setIsStreaming(true);
      onTurnStartRef.current?.();

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const stream = streamTaskAuthoring(
        {
          thread_id: threadId,
          message: trimmed,
          task_id: currentTask?.task_id,
          rewind_last_user_turn: true,
        },
        controller.signal
      );
      await consumeStream(assistantId, stream, controller);
    },
    [consumeStream, isStreaming, threadId, turns]
  );

  const respondToApproval = useCallback(
    async (
      turnId: string,
      decisions: Decision[],
      currentTask?: TaskEditorState
    ) => {
      if (isStreaming || !threadId) return;
      // 결정 step 추가 + status streaming 으로 변경
      setTurns((prev) =>
        prev.map((turn) => {
          if (turn.role !== "assistant" || turn.id !== turnId) return turn;
          if (!turn.pendingApproval) return turn;
          const actions = turn.pendingApproval.action_requests;
          const decisionSteps: AssistantStep[] = decisions.map(
            (decision, idx) => ({
              kind: "approval_decision",
              id: rid(),
              decision,
              originalAction: actions[idx] ?? actions[0],
            })
          );
          return {
            ...turn,
            steps: [...turn.steps, ...decisionSteps],
            status: "streaming",
            pendingApproval: undefined,
          };
        })
      );

      setIsStreaming(true);
      // resume 은 같은 논리적 턴의 연속 — markTurnStart 는 호출하지 않는다 (base 유지).
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const stream = resumeTaskAuthoring(
        {
          thread_id: threadId,
          decisions,
          task_id: currentTask?.task_id,
        },
        controller.signal
      );
      await consumeStream(turnId, stream, controller);
    },
    [consumeStream, isStreaming, threadId]
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const tid = threadId; // 현재 thread 만 soft delete. 새 thread 는 다음 send 가 발급.
    setThreadId(null);
    setTurns([]);
    setIsStreaming(false);
    onTurnEndRef.current?.();
    persistedSignaturesRef.current.clear();
    if (tid) void clearChat(tid);
  }, [threadId]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    // 진행 중이던 assistant turn 을 cancelled 로 마감 — 부분 steps 는 그대로 유지.
    // (running tool step 은 turnActive=false 가 되어 정적 마커로 멈춘다 — turnView 의 기존 동작.)
    setTurns((prev) =>
      prev.map((turn) =>
        turn.role === "assistant" && turn.status === "streaming"
          ? { ...turn, status: "cancelled" }
          : turn
      )
    );
    onTurnEndRef.current?.();
  }, []);

  const revertTurn = useCallback(
    (turnId: string): TaskEditorState | undefined => {
      const found = turns.find(
        (t) => t.role === "assistant" && t.id === turnId
      );
      if (!found || found.role !== "assistant") return undefined;
      return found.taskBefore;
    },
    [turns]
  );

  const switchThread = useCallback(async (targetThreadId: string) => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    onTurnEndRef.current?.();
    setIsLoadingHistory(true);
    try {
      const loaded = await loadSpecificChat(targetThreadId);
      if (loaded) {
        // 로드한 turn 들의 signature 미리 등록 — persist effect 가 다시 PUT 해 BE 의
        // thread.updated_at 이 NOW 로 갱신되는 부작용 방지 (위 loadLatestChat 과 동일 이유).
        const sigs = new Map<string, string>();
        loaded.turns.forEach((t) => sigs.set(t.id, JSON.stringify(t)));
        persistedSignaturesRef.current = sigs;
        setThreadId(loaded.threadId);
        setTurns(loaded.turns);
      }
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  return {
    threadId,
    turns,
    isStreaming,
    isLoadingHistory,
    send,
    editLastUserTurn,
    respondToApproval,
    clear,
    cancel,
    revertTurn,
    switchThread,
  };
}

function reduceAssistantTurn(
  turn: AssistantTurn,
  event: SSEEvent
): AssistantTurn {
  switch (event.event) {
    case "session":
      return turn;

    case "tool_call": {
      const step: AssistantStep = {
        kind: "tool",
        id: rid(),
        name: event.data.name,
        args: event.data.args,
        startedAt: Date.now(),
        status: "running",
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "tool_result": {
      // 같은 name 의 "가장 먼저 시작된(running)" step 부터 done 처리 (FIFO).
      // tool_call/tool_result 에 id 가 없어 name + 순서로 짝짓는다 — 결과는 호출
      // 순서대로 온다고 가정. (LIFO 로 매칭하면 read_file 을 여러 개 동시에 호출할 때
      // 결과가 엉뚱한 행에 붙어 파일명과 내용이 어긋난다.)
      let updated = false;
      const nextSteps = turn.steps.map((step) => {
        if (
          !updated &&
          step.kind === "tool" &&
          step.name === event.data.name &&
          step.status === "running"
        ) {
          updated = true;
          return {
            ...step,
            status: "done" as const,
            result: event.data.preview,
            finishedAt: Date.now(),
          };
        }
        return step;
      });
      return { ...turn, steps: nextSteps };
    }

    case "message_chunk": {
      const last = turn.steps[turn.steps.length - 1];
      if (last?.kind === "text") {
        const merged: AssistantStep = {
          ...last,
          content: last.content + event.data.delta,
        };
        return {
          ...turn,
          steps: [...turn.steps.slice(0, -1), merged],
        };
      }
      const step: AssistantStep = {
        kind: "text",
        id: rid(),
        content: event.data.delta,
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "pending_approval":
      return {
        ...turn,
        status: "awaiting_approval",
        pendingApproval: event.data,
      };

    case "task_patch":
      return { ...turn, taskChanged: true };

    case "todo_update":
      // todo_update 는 매번 전체 목록 — append 아니라 치환.
      return { ...turn, todos: event.data.todos };

    case "final":
      return {
        ...turn,
        status: "complete",
        taskChanged: event.data.task_changed,
      };

    case "debug":
      return {
        ...turn,
        debug: {
          trace_id: event.data.trace_id,
          execution_time_ms: event.data.execution_time_ms,
        },
      };

    case "error":
      return {
        ...turn,
        status: "error",
        error: { type: event.data.type, message: event.data.message },
      };

    default:
      return turn;
  }
}
